<script setup lang="ts">
  import { getCurrentInstance, nextTick, onBeforeMount, onMounted, ref, watch } from 'vue';

  defineOptions({
    inheritAttrs: false,
  });

  const props = defineProps({
    content: { type: String },
  });

  const html = ref<HTMLElement>();

  // Przy hydracji skrypty z serwerowego HTML-a wykonały się już podczas parsowania
  // strony, a Vue nadpisuje potem innerHTML świeżymi, nieaktywnymi węzłami.
  // Rozpoznajemy ten przypadek, żeby nie uruchomić tego samego kodu drugi raz.
  let hydrated = false;

  onBeforeMount(() => {
    hydrated = Boolean(getCurrentInstance()?.vnode.el);
  });

  function pendingScripts(): Element[] {
    return Array.from(html.value?.querySelectorAll('script:not([data-activated])') ?? []);
  }

  // Skrypty wstawione przez innerHTML nie są wykonywane przez przeglądarkę, więc
  // odtwarzamy je jako nowe elementy.
  function activateScripts() {
    pendingScripts().forEach((script) => {
      const activated = document.createElement('script');

      for (const attribute of Array.from(script.attributes)) {
        activated.setAttribute(attribute.name, attribute.value);
      }

      activated.setAttribute('data-activated', '');
      activated.textContent = script.textContent;

      script.replaceWith(activated);
    });
  }

  function markScriptsAsActivated() {
    pendingScripts().forEach((script) => script.setAttribute('data-activated', ''));
  }

  onMounted(() => {
    if (hydrated) {
      markScriptsAsActivated();
      return;
    }

    activateScripts();
  });

  watch(() => props.content, () => {
    nextTick(() => activateScripts());
  });
</script>
<template>
  <SfSection v-if="content">
    <div ref="html" class="html-block" v-html="content" />
  </SfSection>
</template>
<style scoped lang="scss">
  @use "@storefront/styles/mixins/media";

  @include media.query(lg) {
    .html-block {
      padding: 0 0.625rem;
    }
  }
</style>
